import { useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, ListItemText,
  DialogActions, Button, Box, Typography, List, ListItemButton, Chip, Stack, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { AlwaysScrollbar } from '../../../components/ui/AlwaysScrollbar';
import {
  PLANTILLAS_DE_GUSTAVO, eleccionesPorDefecto, renderizarPlantilla, camposLibres,
  type PlantillaDeGustavo, type Elecciones,
} from '../plantillas';

/**
 * Lista a la izquierda, opciones cerradas como chips y vista previa a la derecha. Lo que se
 * elige con un toque entra resuelto; al agente le quedan solo los corchetes.
 */
/**
 * Las plantillas del composer de Gustavo. Hasta el 30/10/2026 vivían detrás de un menú
 * "⋯" junto con Adjuntar; ahora se abren desde el chip "Plantillas" de las acciones
 * rápidas y el clip quedó a la vista.
 */
export function PlantillasDialog({ onClose, onInsertar }: { onClose: () => void; onInsertar: (texto: string) => void }) {
  const golden = P.brand.golden;
  const theme = useTheme();
  const angosto = useMediaQuery(theme.breakpoints.down('sm'));
  const [elegida, setElegida] = useState<PlantillaDeGustavo>(PLANTILLAS_DE_GUSTAVO[0]);
  const [elecciones, setElecciones] = useState<Elecciones>(() => eleccionesPorDefecto(PLANTILLAS_DE_GUSTAVO[0]));
  // Solo la LISTA scrollea (con la barra dorada del CRM); el panel de la derecha queda fijo.
  const listaRef = useRef<HTMLDivElement | null>(null);

  const cambiarPlantilla = (p: PlantillaDeGustavo) => { setElegida(p); setElecciones(eleccionesPorDefecto(p)); };
  const texto = useMemo(() => renderizarPlantilla(elegida, elecciones), [elegida, elecciones]);

  const elegir = (rotulo: string, valor: string) => setElecciones((prev) => ({ ...prev, [rotulo]: valor }));
  const alternar = (rotulo: string, valor: string, actuales: readonly string[]) =>
    setElecciones((prev) => ({ ...prev, [rotulo]: actuales.includes(valor) ? actuales.filter((v) => v !== valor) : [...actuales, valor] }));

  const chip = (activo: boolean) => ({
    height: 24, fontSize: 12,
    bgcolor: activo ? alpha(golden, 0.22) : 'transparent',
    color: activo ? golden : 'text.secondary',
    border: `1px solid ${alpha(golden, activo ? 0.6 : 0.25)}`,
    '&:hover': { bgcolor: alpha(golden, activo ? 0.28 : 0.1) },
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={angosto}>
      <DialogTitle sx={{ pb: 1 }}>
        Plantillas
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
          Elegí las opciones con un toque. Lo que queda entre corchetes es lo único que escribís.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{
        p: 0, display: 'flex', flexDirection: angosto ? 'column' : 'row',
        height: angosto ? 'auto' : 'min(82vh, 720px)', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'relative', width: angosto ? '100%' : 250, flexShrink: 0,
          maxHeight: angosto ? '38vh' : '100%',
          borderRight: angosto ? 'none' : `1px solid ${alpha(golden, 0.15)}`,
          borderBottom: angosto ? `1px solid ${alpha(golden, 0.15)}` : 'none',
        }}>
        <Box ref={listaRef} sx={{ height: '100%', overflowY: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <List dense disablePadding>
          {PLANTILLAS_DE_GUSTAVO.map((p) => (
            <ListItemButton
              key={p.id}
              selected={p.id === elegida.id}
              onClick={() => cambiarPlantilla(p)}
              sx={{ alignItems: 'flex-start', py: 1.25, '&.Mui-selected': { bgcolor: alpha(golden, 0.12) } }}
            >
              <ListItemText
                primary={p.titulo}
                secondary={`${p.descripcion} Escribís ${camposLibres(p)} datos.`}
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 12, lineHeight: 1.35 }}
              />
            </ListItemButton>
          ))}
        </List>
        </Box>
        <AlwaysScrollbar scrollRef={listaRef} />
        </Box>

        <Box sx={{ flex: 1, p: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          <Stack spacing={0.5}>
            {elegida.campos.filter((c) => c.tipo !== 'libre').map((c) => (
              <Stack key={c.rotulo} direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={0.75}>
                <Typography variant="caption" sx={{ width: 96, color: 'text.secondary', fontWeight: 600 }}>{c.rotulo}</Typography>
                {c.tipo === 'opcion' && c.opciones.map((o) => (
                  <Chip key={o} label={o} size="small" clickable onClick={() => elegir(c.rotulo, o)} sx={chip(elecciones[c.rotulo] === o)} />
                ))}
                {c.tipo === 'multi' && c.opciones.map((o) => {
                  const actuales = (elecciones[c.rotulo] as readonly string[] | undefined) ?? c.defecto;
                  return <Chip key={o} label={o} size="small" clickable onClick={() => alternar(c.rotulo, o, actuales)} sx={chip(actuales.includes(o))} />;
                })}
              </Stack>
            ))}
          </Stack>

          <Box sx={{ borderRadius: 1.5, border: `1px solid ${alpha(golden, 0.2)}`, bgcolor: alpha(golden, 0.04), p: 1.5, flexShrink: 1, minHeight: 0, overflow: 'hidden' }}>
            <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.5, color: 'text.primary' }}>
              {texto}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" onClick={() => onInsertar(texto)}
          sx={{ bgcolor: golden, color: '#0A0A0A', '&:hover': { bgcolor: P.brand.goldenLight } }}>
          Insertar en el mensaje
        </Button>
      </DialogActions>
    </Dialog>
  );
}
